export const OCCUPATION_CODE_KEYS = ['noc', 'anzsco', 'soc'] as const;

export type OccupationCodeKey = (typeof OCCUPATION_CODE_KEYS)[number];

export interface OccupationCodes {
    anzsco?: number | null;
    noc?: string | null;
    soc?: number | null;
}

export const PROFILE_FIELD_LABEL_KEYS: Record<string, string> = {
    accompanying: 'accompanying',
    address: 'address',
    anzsco: 'anzsco',
    city: 'city',
    criminal_record: 'criminalRecordShort',
    currency: 'currency',
    field_of_study_au: 'fieldOfStudyAU',
    field_of_study_au_en: 'fieldOfStudyAUEn',
    field_of_study_ca: 'fieldOfStudyCA',
    field_of_study_ca_en: 'fieldOfStudyCAEn',
    field_of_study_uk: 'fieldOfStudyUK',
    field_of_study_uk_en: 'fieldOfStudyUKEn',
    file_number: 'fileNumber',
    has_residency_proof: 'hasResidencyProofQuestion',
    language_setting: 'languageSetting',
    level: 'level',
    middlename: 'middleName',
    noc: 'noc',
    occupation_au: 'occupationAU',
    occupation_au_en: 'occupationAUEn',
    occupation_ca: 'occupationCA',
    occupation_ca_en: 'occupationCAEn',
    occupation_uk: 'occupationUK',
    occupation_uk_en: 'occupationUKEn',
    postal_code: 'postalCode',
    role: 'role',
    self_employed: 'selfEmployed',
    soc: 'soc',
    status: 'status',
    test_date: 'testDate',
    zip: 'zip',
};

export function hasProfileValue(value: unknown): boolean {
    return value !== null && value !== undefined && value !== '';
}

export function presentOccupationCodes(
    record: OccupationCodes | null | undefined
): [OccupationCodeKey, string | number][] {
    if (!record) {
        return [];
    }

    return OCCUPATION_CODE_KEYS.flatMap((key) => {
        const value = record[key];
        return hasProfileValue(value) ? [[key, value as string | number]] : [];
    });
}

function formatDate(date: Date | string, language: string): string {
    const stringDate = date.toString();
    const [year, month, day] = stringDate.split('-');
    if (!year || !month || !day) {
        return stringDate;
    }

    const months =
        language === 'EN'
            ? [
                  'January',
                  'February',
                  'March',
                  'April',
                  'May',
                  'June',
                  'July',
                  'August',
                  'September',
                  'October',
                  'November',
                  'December',
              ]
            : [
                  'Janvier',
                  'Février',
                  'Mars',
                  'Avril',
                  'Mai',
                  'Juin',
                  'Juillet',
                  'Août',
                  'Septembre',
                  'Octobre',
                  'Novembre',
                  'Décembre',
              ];

    const monthName = months[parseInt(month, 10) - 1];
    if (!monthName) {
        return stringDate;
    }

    return `${monthName} ${parseInt(day, 10)}, ${year}`;
}

export function formatProfileValue(
    value: unknown,
    t: (key: string, options?: { lng?: string }) => string,
    language: string,
    fieldKey?: string
): string {
    if (typeof value === 'boolean') {
        return value ? t('yes', { lng: language }) : t('no', { lng: language });
    }

    if (fieldKey === 'file_number') {
        return String(value);
    }

    if (value instanceof Date) {
        return formatDate(value, language);
    }

    const isDateFieldKey =
        !!fieldKey &&
        (fieldKey.endsWith('_date') ||
            fieldKey === 'start' ||
            fieldKey === 'end' ||
            fieldKey === 'residence_since');

    if (typeof value === 'string' && isDateFieldKey && /^\d{4}-\d{2}-\d{2}/.test(value)) {
        return formatDate(value, language);
    }

    return String(value);
}
